import type { RecapDoc } from '../../shared/recap';
import { orderPins, type PinEvent } from '../../shared/events';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { isGiviSnap, isUnseenBy } from './feed';
import { useSeenTalk, waitingOnMe } from './seenTalk';
import { DEFAULT_LIMITS, dayKey } from '../../shared/constants';
import type {
  DailyDoc,
  Limits,
  MessageDoc,
  UserDoc,
} from '../../shared/types';
import { watchMembership, type Membership } from './auth';
import {
  hiddenUidsOf,
  scrubMessages,
  scrubPosts,
  setHidden,
  useHidden,
  withoutHiddenUsers,
} from './hidden';
import {
  canPromptInstall,
  installState,
  onInstallAvailabilityChange,
  type InstallState,
} from './notifications';
import {
  fetchAllMedia,
  fetchAllMessages,
  fetchAround,
  fetchMediaPage,
  fetchOlderMessages,
  watchDay,
  watchLimits,
  viewerEmail,
  visibleRows,
  watchMessage,
  watchMessages,
  watchPins,
  watchRecap,
  type PinEntry,
  watchSavedDailies,
  watchToday,
  watchTyping,
  watchUsers,
  clearTyping,
  setTyping,
  type DayPost,
  type SavedDaily,
  type WithId,
} from './data';
import { forget, hear, whoIsTyping, type Heard } from './typing';
import { TYPING_REFRESH_MS } from '../../shared/constants';

/**
 * Who else is typing in the chat, as uids.
 *
 * A second clock runs only while somebody is showing, so a line whose writer
 * went quiet comes down on time even if nothing else changes on the screen.
 */
export function useWhoIsTyping(enabled: boolean, me: string): string[] {
  const [heard, setHeard] = useState<Heard>({});

  useEffect(() => {
    if (!enabled) return;
    return watchTyping((changes) =>
      setHeard((was) => hear(was, changes, Date.now(), viewerEmail())),
    );
  }, [enabled]);

  const anyone = Object.keys(heard).some((uid) => uid !== me);
  useEffect(() => {
    if (!anyone) return;
    const id = window.setInterval(() => setHeard((was) => forget(was, Date.now())), 1000);
    return () => window.clearInterval(id);
  }, [anyone]);

  return enabled ? whoIsTyping(heard, Date.now(), me) : [];
}

/**
 * Tells the family you are typing while the draft has something in it.
 *
 * Refreshed at most every TYPING_REFRESH_MS rather than on each key, which at
 * a family's typing speed is a write a second each. Cleared as soon as the
 * draft is empty -- which sending makes it -- and on leaving the chat or the
 * app going to the background, so the line never outlives the person.
 *
 * A stamp that fails to write is not worth an error on screen; the worst of
 * it is a line that does not show.
 */
export function useTypingSignal(uid: string, draft: string, quiet: boolean): void {
  const sentAt = useRef(0);
  const showing = useRef(false);

  const stop = () => {
    if (!showing.current) return;
    showing.current = false;
    sentAt.current = 0;
    void clearTyping(uid).catch(() => undefined);
  };

  useEffect(() => {
    if (draft.trim() === '') return stop();
    const now = Date.now();
    if (showing.current && now - sentAt.current < TYPING_REFRESH_MS) return;
    sentAt.current = now;
    showing.current = true;
    void setTyping(uid, quiet).catch(() => undefined);
  }, [draft, uid, quiet]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') stop();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      stop();
    };
  }, [uid]);
}

export interface InstallStatus {
  state: InstallState;
  /** True when the browser will open a real install dialog for us. */
  canPrompt: boolean;
}

/**
 * Install state that keeps up. Reading installState() once at render misses
 * the moment someone installs, which on Android happens in the same window
 * and would otherwise leave them staring at a step they have finished.
 */
export function useInstallStatus(): InstallStatus {
  const [status, setStatus] = useState<InstallStatus>(() => ({
    state: installState(),
    canPrompt: canPromptInstall(),
  }));

  useEffect(() => {
    const refresh = () =>
      setStatus({ state: installState(), canPrompt: canPromptInstall() });

    const unsubscribe = onInstallAvailabilityChange(refresh);
    // display-mode flips without a reload when a desktop PWA installs.
    const media = window.matchMedia('(display-mode: standalone)');
    media.addEventListener('change', refresh);

    return () => {
      unsubscribe();
      media.removeEventListener('change', refresh);
    };
  }, []);

  return status;
}

export function useMembership(): Membership {
  const [membership, setMembership] = useState<Membership>({ state: 'loading' });
  useEffect(() => watchMembership(setMembership), []);
  return membership;
}

/**
 * Every data hook takes `enabled` so that nothing queries Firestore before
 * membership is confirmed. A non-member's query would be denied by rules, but
 * firing it at all produces noisy console errors and wasted reads.
 */
export function useUsers(enabled: boolean): Record<string, UserDoc> {
  const [users, setUsers] = useState<Record<string, UserDoc>>({});
  useEffect(() => {
    if (!enabled) return;
    return watchUsers(setUsers);
  }, [enabled]);
  // The member list is what says which uid the test account has, so this is
  // where every other hook learns what to hide.
  const hidden = useMemo(() => hiddenUidsOf(users, viewerEmail()), [users]);
  useEffect(() => setHidden(hidden), [hidden]);
  return useMemo(() => withoutHiddenUsers(users, hidden), [users, hidden]);
}

export interface Pinned {
  /** The pin's own document, which is what taking it down deletes. */
  id: string;
  message: WithId<MessageDoc>;
  /** Who pinned it. */
  by: string;
  /** What, when, and whether to remind, for a pin that is an event. */
  event?: PinEvent;
}

/**
 * The messages pinned over the chat, each followed to the message itself so
 * an edit shows and a deletion takes its pin with it. Events still to come
 * first, soonest first, then plain pins, newest first; events that have been
 * and gone are left out (shared/events.ts). One message pinned twice -- the
 * old single pin and a new one -- is shown once.
 */
export function usePinned(enabled: boolean): Pinned[] {
  const [pins, setPins] = useState<PinEntry[]>([]);
  const [messages, setMessages] = useState<Record<string, WithId<MessageDoc> | null>>({});
  useEffect(() => {
    if (!enabled) return;
    return watchPins(setPins);
  }, [enabled]);
  const ids = [...new Set(pins.map((pin) => pin.messageId))].sort();
  const key = ids.join(' ');
  useEffect(() => {
    const stops = ids.map((id) => watchMessage(id, (message) => setMessages((now) => ({ ...now, [id]: message }))));
    return () => stops.forEach((stop) => stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return useMemo(() => {
    const seen = new Set<string>();
    const shown: Pinned[] = [];
    for (const pin of orderPins(pins, dayKey(new Date()))) {
      if (seen.has(pin.messageId)) continue;
      const message = messages[pin.messageId];
      // Matched to the pin, so a newly pinned one never shows as another while it loads.
      if (!message || message.id !== pin.messageId || message.data.deletedAt) continue;
      seen.add(pin.messageId);
      shown.push({ id: pin.id, message, by: pin.by, ...(pin.event ? { event: pin.event } : {}) });
    }
    return shown;
  }, [pins, messages]);
}

/** The Sunday recap that is out, or null. */
export function useRecap(enabled: boolean): RecapDoc | null {
  const [recap, setRecap] = useState<RecapDoc | null>(null);
  useEffect(() => {
    if (!enabled) return;
    return watchRecap(setRecap);
  }, [enabled]);
  return recap;
}

export function useLimits(enabled: boolean): Limits {
  const [limits, setLimits] = useState<Limits>({ ...DEFAULT_LIMITS });
  useEffect(() => {
    if (!enabled) return;
    return watchLimits(setLimits);
  }, [enabled]);
  return limits;
}

const CHAT_PAGE = 100;
/** Three columns of thumbnails; twenty rows is a long scroll before refilling. */
const GALLERY_PAGE = 60;

export interface ChatState {
  messages: WithId<MessageDoc>[];
  /** False once the server returns fewer messages than we asked for. */
  hasMore: boolean;
  loadingMore: boolean;
  loadOlder: () => void;
}

/**
 * The newest page stays live; everything older is fetched once by cursor and
 * held in front of it. Nine years of archive cannot change, so subscribing to
 * it would be paying for updates that never come.
 */
export function useMessages(enabled: boolean): ChatState {
  const [live, setLive] = useState<WithId<MessageDoc>[]>([]);
  const [older, setOlder] = useState<WithId<MessageDoc>[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [drained, setDrained] = useState(false);
  // What the listener last said, and whether anybody has paged back past it.
  const lastLive = useRef<WithId<MessageDoc>[]>([]);
  const pagedBack = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    return watchMessages((rows) => {
      // The live page is the newest hundred, so every new message pushes the
      // oldest one off its far end. Once older history has been fetched from
      // the old edge, that row is in neither half -- it just vanished, and any
      // reply quoting it lost its parent. Hand it to the older half instead.
      // Before anything has been paged back there is no seam to fall into:
      // the next fetch starts from the new edge and picks it up again.
      const fell = pagedBack.current ? fellOffTheEnd(lastLive.current, rows) : [];
      lastLive.current = rows;
      if (fell.length) setOlder((current) => joinOlder(current, fell));
      setLive(rows);
    }, CHAT_PAGE);
  }, [enabled]);

  // Filtered here rather than in data.ts, after the page counts below have
  // been read: a hidden quiet row still fills its page.
  const hidden = useHidden();
  const messages = useMemo(
    () => {
      // A row in both halves shows once, as the live copy: that one still
      // hears its likes and edits.
      const inLive = new Set(live.map((m) => m.id));
      const behind = older.filter((m) => !inLive.has(m.id));
      return scrubMessages(visibleRows([...behind, ...live]), hidden);
    },
    [older, live, hidden],
  );

  // Until the live page is full there is nothing behind it to fetch.
  const hasMore = !drained && live.length >= CHAT_PAGE;

  const loadOlder = () => {
    // The cursor is the oldest row fetched, hidden or not.
    const oldest = older[0] ?? live[0];
    if (!oldest || !hasMore || loadingMore) return;
    setLoadingMore(true);
    pagedBack.current = true;
    void fetchOlderMessages(oldest, CHAT_PAGE)
      .then((rows) => {
        if (rows.length < CHAT_PAGE) setDrained(true);
        if (rows.length) setOlder((current) => joinOlder(rows, current));
      })
      .finally(() => setLoadingMore(false));
  };

  return { messages, hasMore, loadingMore, loadOlder };
}

/** Rows the live page held before that are now behind its oldest edge. */
function fellOffTheEnd(
  before: WithId<MessageDoc>[],
  after: WithId<MessageDoc>[],
): WithId<MessageDoc>[] {
  const edge = after[0] ? millisOf(after[0].data.createdAt) : null;
  if (edge === null) return [];
  const still = new Set(after.map((m) => m.id));
  // Only rows older than the edge: one missing from inside the page was
  // deleted, not pushed out, and must stay gone.
  return before.filter((m) => {
    const at = millisOf(m.data.createdAt);
    return !still.has(m.id) && at !== null && at < edge;
  });
}

/** Two runs of older history, oldest first, without a row twice. */
function joinOlder(
  first: WithId<MessageDoc>[],
  second: WithId<MessageDoc>[],
): WithId<MessageDoc>[] {
  const seen = new Set<string>();
  return [...first, ...second].filter((m) => !seen.has(m.id) && !!seen.add(m.id));
}

export interface ThreadWindow {
  messages: WithId<MessageDoc>[];
  loading: boolean;
  /** False when the message is gone, so the screen can say so plainly. */
  found: boolean;
}

/**
 * The thread around one message, for arriving from a search result. This is a
 * separate view from the live tail rather than an extension of it: the two
 * cannot be stitched together without loading every message in between, which
 * for a result from 2017 is the whole archive.
 */
export function useThreadAround(id: string | null): ThreadWindow {
  const [messages, setMessages] = useState<WithId<MessageDoc>[]>([]);
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(true);

  useEffect(() => {
    if (!id) {
      setMessages([]);
      setFound(true);
      return;
    }
    let live = true;
    setLoading(true);
    void fetchAround(id)
      .then((result) => {
        if (!live) return;
        setMessages(result.messages);
        setFound(result.found);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [id]);

  const hidden = useHidden();
  const shown = useMemo(() => scrubMessages(messages, hidden), [messages, hidden]);
  return { messages: shown, loading, found };
}

export interface SearchState {
  /** Null until the archive has been pulled down for the first time. */
  results: WithId<MessageDoc>[] | null;
  loading: boolean;
  term: string;
  setTerm: (term: string) => void;
  /** Only messages that name the reader, @FamBam included. */
  mentionsOnly: boolean;
  setMentionsOnly: (on: boolean) => void;
}

/**
 * What a search finds, newest first as the archive arrives.
 *
 * Typed words match anywhere in the text. With `mentioning`, only messages
 * that name that person count -- and then nothing needs typing at all: every
 * one of them is the answer, and words only narrow it. @FamBam is stored as
 * the whole family by name, so it counts for everyone without asking.
 */
export function searchMessages(
  all: WithId<MessageDoc>[],
  term: string,
  mentioning: string | null,
): WithId<MessageDoc>[] {
  const needle = term.trim().toLowerCase();
  if (!needle && !mentioning) return [];
  return all.filter(
    (m) =>
      (!mentioning || (m.data.mentions ?? []).includes(mentioning)) &&
      (!needle || (m.data.text ?? '').toLowerCase().includes(needle)),
  );
}

/**
 * Firestore has no substring search, so the archive is pulled down once and
 * matched in memory. At four thousand messages that is a few megabytes and a
 * scan too fast to notice; it is also the only way to search text at all
 * without paying for a second search service.
 */
export function useMessageSearch(enabled: boolean, uid: string): SearchState {
  const [all, setAll] = useState<WithId<MessageDoc>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const [mentionsOnly, setMentionsOnly] = useState(false);

  useEffect(() => {
    if (!enabled || all || loading) return;
    setLoading(true);
    void fetchAllMessages()
      .then(setAll)
      .finally(() => setLoading(false));
  }, [enabled]);

  const hidden = useHidden();
  const results = useMemo(() => {
    if (!all) return null;
    return searchMessages(scrubMessages(all, hidden), term, mentionsOnly ? uid : null);
  }, [all, term, hidden, mentionsOnly, uid]);

  return { results, loading, term, setTerm, mentionsOnly, setMentionsOnly };
}

export interface GalleryState {
  messages: WithId<MessageDoc>[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

/*
 * A deleted photo's tombstone has nothing left to draw. deletePost now clears
 * hasMedia with the rest, but the ones deleted before that still carry it, so
 * the media queries keep returning them and only this keeps them off screen.
 */
function withoutDeleted<T extends { data: MessageDoc }>(rows: T[]): T[] {
  return rows.filter((row) => !row.data.deletedAt);
}

/** Every message carrying media, newest first, a page at a time. */
export function useGallery(enabled: boolean): GalleryState {
  const [messages, setMessages] = useState<WithId<MessageDoc>[]>([]);
  const [loading, setLoading] = useState(false);
  const [drained, setDrained] = useState(false);
  const [started, setStarted] = useState(false);

  const fetchPage = (after: WithId<MessageDoc> | null) => {
    setLoading(true);
    void fetchMediaPage(after, GALLERY_PAGE)
      .then((rows) => {
        if (rows.length < GALLERY_PAGE) setDrained(true);
        if (rows.length) {
          setMessages((current) => (after ? [...current, ...rows] : rows));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!enabled || started) return;
    setStarted(true);
    fetchPage(null);
  }, [enabled]);

  const loadMore = () => {
    if (loading || drained) return;
    fetchPage(messages[messages.length - 1] ?? null);
  };

  // As with the chat: every row is kept for the cursor, and only the visible
  // ones are handed out.
  const hidden = useHidden();
  const shown = useMemo(
    () => scrubMessages(withoutDeleted(visibleRows(messages)), hidden),
    [messages, hidden],
  );

  return { messages: shown, loading, hasMore: !drained, loadMore };
}

export interface TodayState {
  /** The calendar day, which is where the prompt doc lives. */
  day: string;
  /**
   * The day whose GiviSnaps are on screen, and which a new one joins. The
   * same as `day` once today's prompt has fired, yesterday until it does —
   * because until then yesterday's is the prompt still being answered.
   */
  showing: string;
  daily: DailyDoc | null;
  posts: DayPost[];
  /**
   * Whether the posts have been read yet. An empty day and a day still
   * loading are the same empty array, and a screen opened on a GiviSnap that
   * expired has to say which of the two it is looking at rather than spinning
   * for ever on something that is never going to arrive.
   */
  settled: boolean;
}

export function useToday(enabled: boolean): TodayState {
  /*
   * The day is not a constant, and treating it as one was the same bug as
   * watching too few of them.
   *
   * It used to be worked out once, when this mounted. An app left open across
   * midnight -- which is most of them, since nobody closes a phone's home
   * screen app -- went on watching yesterday and the day before, so the new
   * day's prompt fired into a set of listeners that were not looking at it.
   * Every GiviSnap answering it was invisible until the app was reopened.
   *
   * Checked on a slow loop and again whenever the app comes back to the
   * foreground, which between them catch both the phone that stayed awake and
   * the one that was asleep at midnight.
   */
  const [day, setDay] = useState(() => dayKey(new Date()));

  useEffect(() => {
    const check = () =>
      setDay((was) => {
        const now = dayKey(new Date());
        return now === was ? was : now;
      });

    const id = window.setInterval(check, 60_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  const [state, setState] = useState<{
    daily: DailyDoc | null;
    posts: DayPost[];
    showing: string;
    settled: boolean;
  }>({ daily: null, posts: [], showing: day, settled: false });

  // Re-subscribed when the day turns over, which is what moves the listeners
  // on to it: watchToday reads the clock each time it is called.
  useEffect(() => {
    if (!enabled) return;
    return watchToday((daily, posts, showing, settled) =>
      setState({ daily, posts, showing, settled }),
    );
  }, [enabled, day]);

  const hidden = useHidden();
  const posts = useMemo(() => scrubPosts(state.posts, hidden), [state.posts, hidden]);
  return { day, ...state, posts };
}

/**
 * Everything the family has chosen to keep, newest first.
 *
 * A collection group query across every day, which is the only way to ask the
 * question: kept posts stay where they were written rather than moving to a
 * collection of their own, so there is no one place to read them from.
 */
export function useSavedDailies(enabled: boolean): SavedDaily[] {
  const [saved, setSaved] = useState<SavedDaily[]>([]);
  useEffect(() => {
    if (!enabled) return;
    return watchSavedDailies(setSaved);
  }, [enabled]);
  const hidden = useHidden();
  return useMemo(() => scrubPosts(saved, hidden), [saved, hidden]);
}

export interface MemoryState {
  /** The photo being shown, or null until the archive has been read. */
  photo: WithId<MessageDoc> | null;
  /** The rest of that week, so one memory opens onto the days around it. */
  week: WithId<MessageDoc>[];
  /** Today's date in earlier years, newest year first; empty on a day with none. */
  onThisDay: YearOfPhotos[];
  loading: boolean;
  /** Another one, at random. */
  again: () => void;
}

export interface YearOfPhotos {
  year: number;
  /** In the order they were sent that day. */
  photos: WithId<MessageDoc>[];
}

/**
 * Everything sent on today's date in an earlier year, a group a year.
 *
 * The date is read in the family's own zone, as every day in the app is, so
 * a photo sent late on the 30th is not "on this day" on the 1st.
 */
export function onThisDayOf(rows: WithId<MessageDoc>[], now: Date): YearOfPhotos[] {
  const today = dayKey(now);
  const byYear = new Map<number, WithId<MessageDoc>[]>();
  for (const row of rows) {
    const at = millisOf(row.data.createdAt);
    if (!at) continue;
    const day = dayKey(new Date(at));
    if (day.slice(5) !== today.slice(5) || day.slice(0, 4) >= today.slice(0, 4)) continue;
    const year = Number(day.slice(0, 4));
    byYear.set(year, [...(byYear.get(year) ?? []), row]);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, photos]) => ({
      year,
      photos: photos.sort((a, b) => (millisOf(a.data.createdAt) ?? 0) - (millisOf(b.data.createdAt) ?? 0)),
    }));
}

/** Either side of the shown photo, which is a week with it in the middle. */
const HALF_WEEK_MS = 3.5 * 86_400_000;

/**
 * One photo out of the whole archive, picked at random.
 *
 * The archive is read once and kept, because random access is the point: a
 * cursor only walks backwards from now, and the memory worth finding is the
 * one nobody would have scrolled to.
 */
export function useMemories(enabled: boolean): MemoryState {
  const [fetched, setAll] = useState<WithId<MessageDoc>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState(0);
  const hidden = useHidden();
  const all = useMemo(
    () => fetched && scrubMessages(withoutDeleted(fetched), hidden),
    [fetched, hidden],
  );

  useEffect(() => {
    if (!enabled || fetched || loading) return;
    setLoading(true);
    void fetchAllMedia()
      .then((rows) => {
        setAll(rows);
        setPick(Math.floor(Math.random() * Math.max(1, rows.length)));
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  const photo = all && all.length ? (all[pick % all.length] ?? null) : null;

  const week = useMemo(() => {
    if (!all || !photo) return [];
    const at = millisOf(photo.data.createdAt);
    if (!at) return [];
    return all.filter(
      (row) =>
        row.id !== photo.id &&
        Math.abs((millisOf(row.data.createdAt) ?? 0) - at) <= HALF_WEEK_MS,
    );
  }, [all, photo]);

  const again = () => {
    if (!all || all.length < 2) return;
    // Never the same one twice in a row: "show me another" that shows the same
    // photo reads as a broken button rather than as a coincidence.
    let next = pick;
    while (next === pick) next = Math.floor(Math.random() * all.length);
    setPick(next);
  };

  const onThisDay = useMemo(() => (all ? onThisDayOf(all, new Date()) : []), [all]);

  return { photo, week, onThisDay, loading, again };
}

function millisOf(value: unknown): number | null {
  return (value as { toMillis?: () => number } | null)?.toMillis?.() ?? null;
}

/**
 * An arbitrary day's dailies. Returns today's state untouched when the day
 * asked for is today, so opening a pointer for today does not set up a second
 * listener on the same documents.
 */
export function useDay(day: string | null, enabled: boolean): TodayState | null {
  const [state, setState] = useState<{
    daily: DailyDoc | null;
    posts: DayPost[];
    settled: boolean;
  }>({ daily: null, posts: [], settled: false });

  useEffect(() => {
    if (!enabled || !day) return;
    // Back to unsettled for the new day, or the screen would answer a question
    // about this day with the last one's evidence.
    setState({ daily: null, posts: [], settled: false });
    return watchDay(day, (daily, posts, settled) =>
      // Tagged with the day that was asked for. An archive only ever holds
      // one, but the shape has to match the live screen's, which holds two.
      setState({
        daily,
        posts: posts.map((post) => ({ ...post, day })),
        settled,
      }),
    );
  }, [enabled, day]);

  const hidden = useHidden();
  const posts = useMemo(() => scrubPosts(state.posts, hidden), [state.posts, hidden]);
  if (!day) return null;
  // An archived day is only ever showing itself; nothing here is waiting on a
  // prompt to fire.
  return { day, showing: day, ...state, posts };
}

export interface UnreadState {
  count: number;
  /** Everything up to now read -- or up to `through`, if that is later. */
  markRead: (through?: number) => void;
}

/** Counts messages newer than the last time the chat tab was opened. */
export function useUnread(
  messages: WithId<MessageDoc>[],
  uid: string,
): UnreadState {
  const [seenAt, setSeenAt] = useState<number>(() => readChatSeenAt());

  /*
   * Up to now, or up to the newest message if that is stamped later: the
   * server's clock and the phone's rarely agree, and a message stamped a
   * second ahead of this phone would otherwise stay unread while on screen.
   */
  const markRead = (through = 0) => {
    const now = Math.max(Date.now(), through);
    setSeenAt(now);
    try {
      localStorage.setItem(CHAT_SEEN_KEY, String(now));
    } catch {
      // Private mode or blocked storage: the badge stops persisting, which is
      // a cosmetic loss, so there is nothing to recover from here.
    }
  };

  const count = messages.filter((m) => {
    if (m.data.authorUid === uid) return false;
    // Pointers are still written, for the notification and for the record, but
    // chat no longer draws them. Counting one would put a number on the tab
    // for a row that is not there when you open it.
    if (m.data.kind === 'dailyPointer') return false;
    // Nor a deleted one, which the chat no longer draws either.
    if (m.data.deletedAt) return false;
    const at = m.data.createdAt as { toMillis?: () => number } | null;
    return (at?.toMillis?.() ?? 0) > seenAt;
  }).length;

  return { count, markRead };
}

/**
 * Keeps the chat read while you are looking at it.
 *
 * It was marked read on opening the tab and whenever the count of loaded
 * messages changed -- but the chat holds only its newest page, so once there
 * are a page of messages a new one pushes the oldest out and the count stays
 * where it was. A snap or a message that arrived with the chat open then sat
 * unread until you left and came back.
 *
 * So: read when the newest message changes, and when the app comes back into
 * view on the chat. Never while the app is out of sight -- a chat left open on
 * a locked phone has not been read.
 */
export function useReadWhileOpen(
  open: boolean,
  messages: WithId<MessageDoc>[],
  markRead: (through?: number) => void,
): void {
  const newest = messages[messages.length - 1];
  const newestAt =
    (newest?.data.createdAt as { toMillis?: () => number } | null)?.toMillis?.() ?? 0;
  const mark = useRef(markRead);
  mark.current = markRead;

  useEffect(() => {
    if (!open) return;
    const read = () => {
      if (document.visibilityState !== 'hidden') mark.current(newestAt);
    };
    read();
    document.addEventListener('visibilitychange', read);
    return () => document.removeEventListener('visibilitychange', read);
  }, [open, newest?.id, newestAt]);
}

export interface UnseenState {
  any: boolean;
}

/**
 * Whether anybody else has posted a GiviSnap you have not opened.
 *
 * This is what chat pointer rows used to say, moved onto the tab the news is
 * about. A dot rather than a count: the question is whether there is anything
 * new to go and see, and that screen shows everyone at once.
 *
 * Read off the post own seenBy, which the pager already writes when it puts a
 * photo on screen. The first attempt kept a last-opened-the-tab mark on the
 * device, the way the chat badge does, and that could not work here: today is
 * the app default route, so every launch landed on the GiviSnaps tab and
 * stamped everything seen before anyone had looked at anything. seenBy also
 * answers the real question -- not having seen somebody GiviSnap is about
 * their photo, not about which tab was last visited -- and being on the
 * document it follows you between devices.
 */
export function useUnseenDailies(posts: DayPost[], uid: string): UnseenState {
  /*
   * GiviSnaps only. The day's posts include stories, which live on the camera
   * tab -- so an unwatched story lit this dot, and nothing on the screen the
   * dot points at could ever put it out. Read through the same two tests the
   * feed uses, so the dot and the caught-up line cannot disagree about what is
   * new.
   */
  const seen = useSeenTalk();
  /*
   * Comments on your own count too, and keep the dot lit until you open them.
   * Looking at the card is not reading what was said under it, so unlike a
   * new GiviSnap this one waits for the comments sheet. Kept on the device,
   * like the count on the comment button that it agrees with.
   */
  const any = posts.some(
    (post) => isGiviSnap(post) && (isUnseenBy(post, uid) || waitingOnMe(post, uid, seen)),
  );

  return { any };
}

const CHAT_SEEN_KEY = 'givisnap:chatSeenAt';

/**
 * When the chat tab was last opened.
 *
 * Exported because the unread line is drawn from the same mark as the badge,
 * and has to read it before the badge clears it: opening the tab is what marks
 * everything read, so by the time any effect runs the answer is "just now".
 * Chat reads this while rendering, which is strictly earlier.
 */
export function readChatSeenAt(): number {
  try {
    return Number(localStorage.getItem(CHAT_SEEN_KEY) ?? 0);
  } catch {
    return 0;
  }
}

/** Relative time short enough for a chat row: "now", "4m", "2h", "Tue". */
export function shortTime(value: unknown): string {
  const at = value as { toDate?: () => Date } | null;
  const date = at?.toDate?.();
  if (!date) return '';

  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Closer than this to the end counts as reading the newest. */
const NEAR_NEWEST_PX = 120;

/**
 * A list read oldest at the top and newest at the bottom -- a snap exchange,
 * a GiviSnap's comments -- opening on its newest, the way the chat does.
 *
 * Opened at the top, the newest was off the bottom of the sheet and the thing
 * you came to see was the one thing not on screen. So it opens at the end, and
 * follows what arrives while you are there, and always follows your own; if
 * you have scrolled back to read, it leaves you where you are. A picture that
 * loads late and pushes the end further down is followed too.
 */
export function useFollowNewest(
  list: RefObject<HTMLElement>,
  newest: string | undefined,
  newestIsMine: boolean,
): void {
  const near = useRef(true);

  useLayoutEffect(() => {
    const node = list.current;
    if (!node) return;
    const measure = () => {
      near.current = node.scrollHeight - node.scrollTop - node.clientHeight < NEAR_NEWEST_PX;
    };
    // A picture in it loading late; load does not bubble, so it is caught on the way down.
    const settle = () => {
      if (near.current) node.scrollTop = node.scrollHeight;
    };
    node.addEventListener('scroll', measure, { passive: true });
    node.addEventListener('load', settle, true);
    return () => {
      node.removeEventListener('scroll', measure);
      node.removeEventListener('load', settle, true);
    };
  }, [list]);

  useLayoutEffect(() => {
    const node = list.current;
    if (!node) return;
    if (near.current || newestIsMine) {
      node.scrollTop = node.scrollHeight;
      near.current = true;
    }
  }, [list, newest, newestIsMine]);
}

export function clockTime(value: unknown): string {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Whether the on-screen keyboard is up.
 *
 * A field taking focus is what says so. There is no event for a keyboard, and
 * every attempt to infer one from the size of things has come apart on iOS:
 * in a standalone app the layout viewport shrinks along with the visual one,
 * so `innerHeight - visualViewport.height` stays at nought with the keyboard
 * filling half the screen. Twice that measurement has been the bug — once
 * refusing to hide the bar, once hiding it and putting it straight back.
 *
 * So focus decides, and the viewport only ever overrules it with positive
 * evidence: the height it rests at with nothing focused, compared against the
 * height now. Measured against itself, it does not matter which viewport iOS
 * chose to move. With no such evidence the bar stays hidden, because a bar
 * that will not go is the worse of the two faults.
 *
 * Absent on desktop, where the answer is simply no.
 */
export function useKeyboardUp(): boolean {
  const [up, setUp] = useState(false);
  const settle = useRef<number | undefined>(undefined);
  /** The visual viewport's height with nothing focused. */
  const resting = useRef<number | null>(null);

  useEffect(() => {
    // A keyboard that takes up room is one there is no hardware for.
    if (!navigator.maxTouchPoints) return;

    const view = window.visualViewport;

    const editable = (node: EventTarget | null): boolean => {
      const element = node as HTMLElement | null;
      return Boolean(
        element?.matches?.(
          'input:not([type="file"]):not([type="checkbox"]), textarea, [contenteditable="true"]',
        ),
      );
    };

    const typing = () => editable(document.activeElement);

    const remember = () => {
      if (view && !typing()) resting.current = view.height;
    };
    remember();

    /** Proof that something took a bite out of the screen. */
    const shrunk = (): boolean =>
      view !== null && resting.current !== null && resting.current - view.height > 80;

    /** Proof that nothing did — not merely the absence of proof that it did. */
    const unchanged = (): boolean =>
      view !== null && resting.current !== null && !shrunk();

    const onIn = (event: FocusEvent) => {
      if (!editable(event.target)) return;
      setUp(true);
      /*
       * A keyboard may not actually arrive — a simulator with the host's
       * keyboard attached, or an iPad in a case, focuses a field and raises
       * nothing. Long enough afterwards for the animation to have finished,
       * the viewport is asked, and only a clear "nothing moved" puts the bar
       * back.
       */
      window.clearTimeout(settle.current);
      settle.current = window.setTimeout(() => {
        if (unchanged()) setUp(false);
      }, 600);
    };

    // Settled on the next tick, because moving between two fields fires out
    // and then in, and the bar would flash back in between them.
    const onOut = () => {
      window.setTimeout(() => {
        if (typing()) return;
        setUp(false);
        remember();
      }, 0);
    };

    /*
     * A phone can put the keyboard away while the field keeps focus — iOS has
     * a key for exactly that — so the viewport coming back to its resting
     * height lowers the bar even though focus has not moved.
     */
    const onResize = () => {
      if (!typing()) {
        remember();
        setUp(false);
        return;
      }
      if (shrunk()) setUp(true);
      else if (unchanged()) setUp(false);
    };

    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    view?.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(settle.current);
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
      view?.removeEventListener('resize', onResize);
    };
  }, []);

  return up;
}

/** A Firestore timestamp as a Date, or null while it is still being written. */
export function toDate(value: unknown): Date | null {
  const at = value as { toDate?: () => Date } | null;
  return at?.toDate?.() ?? null;
}

/** A silence this long earns a time marker in the thread. */
const QUIET_MINUTES = 60;

/**
 * The marker to put above a message, or null to run it on from the one before.
 *
 * Every message carrying its own timestamp is clutter in a fast conversation,
 * so the thread only breaks where the reader would otherwise lose the thread of
 * time: a new day, or a long enough gap that the next message is really a new
 * exchange.
 */
export function timeMarkerFor(previous: unknown, current: unknown): string | null {
  const now = toDate(current);
  if (!now) return null;

  const before = toDate(previous);
  if (!before) return dayLabel(now);
  if (before.toDateString() !== now.toDateString()) return dayLabel(now);
  if (now.getTime() - before.getTime() < QUIET_MINUTES * 60_000) return null;
  return clockTime(current);
}

/** "Today" and "Yesterday" by name; anything older gets its date. */
export function dayLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * How long before an unsaved post disappears, as short display text.
 * Hours below a day, because with a 24-hour window "1d left" tells you
 * nothing — "3h left" is the difference between saving it and losing it.
 */
export function timeLeft(expireAt: unknown): string | null {
  const at = expireAt as { toMillis?: () => number } | null;
  const ms = at?.toMillis?.();
  if (!ms) return null;

  const remaining = ms - Date.now();
  if (remaining <= 0) return 'gone';
  if (remaining < 3_600_000) return `${Math.max(1, Math.floor(remaining / 60_000))}m`;
  if (remaining < 86_400_000) return `${Math.floor(remaining / 3_600_000)}h`;
  return `${Math.ceil(remaining / 86_400_000)}d`;
}
